// Phase 7 D3 — Track C T-C6. NotificationToggle row.
//
// One row per alert type with a Switch primitive. Q-10 says non-destructive
// saves close silently — we surface a transient inline status only on
// failure. Q-12 unknown-key handling lives in Notifications.tsx and passes
// `unknown=true` here, which:
//   - greys out the row,
//   - adds a tooltip-style explanatory caption,
//   - replaces the Switch with a "Remove" button that clears the key.

import { copy } from '@dailydeck/shared/copy';
import { Switch } from '../../../components/ui/Switch.js';
import { Button } from '../../../components/ui/Button.js';
import { cn } from '../../../lib/cn.js';

interface NotificationToggleProps {
  alertTypeId: string;
  /** Human-readable label. For known types this is provided by the registry
   *  catalogue in Notifications.tsx; for unknown keys it falls back to the
   *  raw alertTypeId. */
  label: string;
  description?: string;
  checked: boolean;
  disabled?: boolean;
  /** When true, this key is not in the active module registry. Q-12 says
   *  render greyed + offer a remove affordance, NOT silent drop. */
  unknown?: boolean;
  onChange: (next: boolean) => void;
  onRemoveUnknown?: () => void;
}

export function NotificationToggle({
  alertTypeId,
  label,
  description,
  checked,
  disabled,
  unknown,
  onChange,
  onRemoveUnknown,
}: NotificationToggleProps): JSX.Element {
  const id = `notif-toggle-${alertTypeId.replace(/[^A-Za-z0-9_-]/g, '-')}`;
  return (
    <div
      data-testid={`notification-toggle-${alertTypeId}`}
      data-unknown={unknown ? 'true' : undefined}
      className={cn(
        'flex flex-col gap-1 border-b border-border-subtle py-3 last:border-b-0 sm:flex-row sm:items-center sm:justify-between',
        unknown && 'opacity-60',
      )}
    >
      <div className="flex flex-col gap-1">
        <label htmlFor={id} className="text-body-strong text-text-primary">
          {label}
        </label>
        {description !== undefined && (
          <p className="text-caption text-text-muted">{description}</p>
        )}
        {unknown === true && (
          <p
            className="text-caption text-text-muted"
            // Title attribute provides the tooltip — assistive tech also
            // sees it via aria-describedby below.
            title={copy.settings.notifications.unknownTooltip}
            id={`${id}-unknown-help`}
          >
            {copy.settings.notifications.unknownTooltip}
          </p>
        )}
      </div>
      <div className="flex items-center gap-2">
        {unknown === true && onRemoveUnknown !== undefined ? (
          <Button
            variant="secondary"
            size="sm"
            type="button"
            onClick={onRemoveUnknown}
          >
            {copy.settings.notifications.unknownRemove}
          </Button>
        ) : (
          <Switch
            id={id}
            checked={checked}
            disabled={disabled}
            onCheckedChange={onChange}
            aria-label={label}
          />
        )}
      </div>
    </div>
  );
}
